import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Typography
} from "@mui/material";
import { useState } from "react";

import ColorChip from "../../../components/ColorChip";
import MarkdownRenderer from "../../../components/MarkdownRenderer";
import { ViewerArtifacts } from "../../../data/queries/viewer";
import { OverallRiskIndicator, PROCESS_DECISION_DO_NOT_PROCESS, RiskAxisScore } from "../../../types/risk-analysis";
import { hasRiskAnalysisData } from "../../../utils/riskAnalysis";

const RISK_INDICATOR_TO_CHIP_VALUE: Record<OverallRiskIndicator, string> = {
  HIGH: "high",
  MODERATE: "medium",
  MEDIUM: "medium",
  LOW: "low",
  UNKNOWN: "unknown"
};

interface RiskTabProps {
  data: ViewerArtifacts | null;
  isLoading: boolean;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="subtitle1"
    fontWeight="bold"
    color="primary"
    sx={{ mb: 1, mt: 2 }}
  >
    {children}
  </Typography>
);

const RiskScoreIndicator = ({ score, maxScore = 5 }: { score: number; maxScore?: number }) => {
  const normalizedValue = (score / maxScore) * 100;

  const getScoreColor = (value: number) => {
    if (value >= 60) {
      return "error";
    }
    if (value >= 40) {
      return "warning";
    }
    return "success";
  };

  return (
    <Box sx={{ width: "100%", mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" fontWeight="bold">
          {score}/{maxScore}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {score <= 1 ? "Low" : score <= 3 ? "Medium" : "High"}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={normalizedValue}
        color={getScoreColor(normalizedValue) as any}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
};

const RiskAxisCard = ({
  title,
  data
}: {
  title: string;
  data: RiskAxisScore;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Box sx={{ width: "50%" }}>
          <RiskScoreIndicator score={data.score} />
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 2 }}>
        {data.justification}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Accordion
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
        elevation={0}
        disableGutters
        sx={{
          bgcolor: "background.default",
          "&:before": { display: "none" }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" fontWeight="bold" color="primary">
            Supporting Evidence
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {data.evidence.map((item, index) => (
              <ListItem key={index} disableGutters>
                <ListItemText
                  primary={`"${item}"`}
                  primaryTypographyProps={{ variant: "body2", style: { fontStyle: "italic" } }}
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

const AgentPerformanceComparison = ({
  beforeRisk,
  afterRisk
}: {
  beforeRisk: Record<string, any>;
  afterRisk: Record<string, any>;
}) => {
  const riskFactors = [
    { key: "severity", label: "Severity" },
    { key: "likelihood", label: "Likelihood" },
    { key: "financialRisk", label: "Financial Risk" },
    { key: "complianceRisk", label: "Compliance Risk" },
    { key: "operationalRisk", label: "Operational Risk" },
    { key: "timeSensitivity", label: "Time Sensitivity" },
    { key: "reputationalRisk", label: "Reputational Risk" }
  ];

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Risk Mitigation Impact
      </Typography>

      <Grid container spacing={2}>
        {riskFactors.map((factor) => (
          <Grid item xs={12} key={factor.key}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {factor.label}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Before</Typography>
                  <RiskScoreIndicator score={beforeRisk[factor.key]} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">After</Typography>
                  <RiskScoreIndicator score={afterRisk[factor.key]} />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export const RiskTab = ({ data, isLoading }: RiskTabProps) => {
  if (isLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Skeleton variant="text" sx={{ fontSize: "2rem" }} />
        <Skeleton variant="rectangular" height={120} />
        <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!data || !data.riskAnalysis) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Risk Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No risk analysis data available for this interaction.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const riskContent = data.riskAnalysisNormalized;
  const showExtendedRiskAnalysis = false;

  if (!riskContent) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Risk Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No risk analysis data available for this interaction.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (riskContent.process_decision === PROCESS_DECISION_DO_NOT_PROCESS) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Risk Analysis
          </Typography>
          {riskContent.reason_description && (
            <MarkdownRenderer content={riskContent.reason_description} />
          )}
        </Paper>
      </Box>
    );
  }

  const { riskAnalysis } = riskContent;

  if (!hasRiskAnalysisData(riskAnalysis)) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Risk Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No risk analysis data available for this interaction.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 0, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <SectionTitle>Risk Analysis (Beta)</SectionTitle>
            <Typography variant="body1" paragraph>
              {riskAnalysis.overallJustification}
            </Typography>

            <SectionTitle>Recommendations</SectionTitle>
            <List component="ol" sx={{ listStyleType: "decimal", pl: 2 }}>
              {riskAnalysis.recommendations.map((recommendation: string, index: number) => (
                <ListItem key={index} sx={{ display: "list-item" }}>{recommendation}</ListItem>
              ))}
            </List>

            {riskContent.agentPerformance?.summary && (
              <>
                <SectionTitle>Agent Performance</SectionTitle>
                <Typography variant="body1" paragraph>
                  {riskContent.agentPerformance.summary}
                </Typography>
              </>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Overall Risk
                </Typography>
                <ColorChip value={RISK_INDICATOR_TO_CHIP_VALUE[riskAnalysis.overallIndicator] ?? RISK_INDICATOR_TO_CHIP_VALUE.UNKNOWN} field="risk" />
              </Box>

              {riskContent.reason_description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Reason
                  </Typography>
                  <Typography variant="body2">
                    {riskContent.reason_description}
                  </Typography>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        {showExtendedRiskAnalysis && (
          <>
            <SectionTitle>Risk Axes</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <RiskAxisCard
                  title="Severity"
                  data={riskAnalysis.axes.severity}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <RiskAxisCard
                  title="Likelihood"
                  data={riskAnalysis.axes.likelihood}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <RiskAxisCard
                  title="Time Sensitivity"
                  data={riskAnalysis.axes.timeSensitivity}
                />
              </Grid>
            </Grid>

            <SectionTitle>Risk Types</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <RiskAxisCard
                  title="Financial Risk"
                  data={riskAnalysis.riskTypes.financialRisk}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RiskAxisCard
                  title="Compliance Risk"
                  data={riskAnalysis.riskTypes.complianceRisk}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RiskAxisCard
                  title="Operational Risk"
                  data={riskAnalysis.riskTypes.operationalRisk}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RiskAxisCard
                  title="Reputational Risk"
                  data={riskAnalysis.riskTypes.reputationalRisk}
                />
              </Grid>
            </Grid>

            {riskContent.agentPerformance && (
              <Box sx={{ mt: 3 }}>
                <SectionTitle>Agent Risk Mitigation</SectionTitle>
                <AgentPerformanceComparison
                  beforeRisk={riskContent.agentPerformance.beforeCallRisk}
                  afterRisk={riskContent.agentPerformance.afterCallRisk}
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RiskTab;
